/**
 * Quota-resilient localStorage writes.
 *
 * The problem this solves: localStorage has a hard ~5 MB per-origin
 * quota. The chart's bar cache (`mpp:chart:bars:*`) stores ~1500 OHLC
 * bars per symbol/range/interval combo — ~100 KB each. Heavy use
 * accumulates dozens of them and the quota fills. Once full, EVERY
 * `localStorage.setItem` throws `QuotaExceededError` — and callers
 * that wrap the write in `try/catch {}` (which is most of them) lose
 * the write SILENTLY. Net effect: workspace layouts, indicator
 * config, etc. stop persisting with no error anywhere. (Incognito
 * starts with an empty store, which is why it "works" — there's
 * room.)
 *
 * The fix: when a write fails, evict the disposable cache entries
 * (the bar caches — pure performance cache, fully rebuildable from
 * the API) and retry. Critical state always wins room by sacrificing
 * the throwaway cache.
 */

import { ACCOUNT_MODE_KEY, paperStorageKey } from "@/lib/wealth/accountMode";

/**
 * The Real / Paper switch of the Wealth section (lib/wealth/accountMode.ts).
 * While this device is in Paper, a key that holds account data (a saved
 * Forecast plan, the tax setting, a selected account) is read and written
 * under its paper twin, so a demonstration never shows the owner's own plans
 * and never overwrites them. Done here, in the three functions every caller
 * already goes through, so a new Wealth key is separate without its author
 * having to know. Keys that are only layout are shared (the list is in
 * accountMode.ts). The mode key itself is read raw.
 */
function accountKey(key: string): string {
  try {
    if (localStorage.getItem(ACCOUNT_MODE_KEY) !== "paper") return key;
  } catch {
    return key;
  }
  return paperStorageKey(key);
}

/** Key prefixes that are pure, rebuildable cache — safe to drop when
 *  the quota is under pressure. The bar cache is the big one; it just
 *  makes revisiting a chart instant instead of a ~300 ms fetch. */
const DISPOSABLE_PREFIXES = ["mpp:chart:bars:"] as const;

/**
 * Key families NO current code reads or writes — dead widgets and
 * pre-consolidation caches. On long-lived machines they quietly fill the
 * origin's fixed 10MB localStorage: one real browser sat at 10,240KB with
 * ~6.5MB of chartWidget:barsV1:* bars, which made every multi-KB write
 * bounce (hydrate included) while byte-sized probes still fit. Swept at
 * boot; after the first sweep the scan finds nothing and is O(keys) cheap.
 */
const LEGACY_PREFIXES = ["chartWidget:"] as const;

/**
 * Individual retired keys, matched EXACTLY.
 *
 * Exact rather than by prefix because these are superseded versions of keys
 * that are still live: `mpp:advchart:pine-dock:` as a prefix would take `:v2`
 * down with `:v1`, which is the whole thing the version suffix exists to
 * prevent. Add a key here when you bump its `:vN` and the old value would
 * change behaviour if it were still read.
 */
const LEGACY_KEYS = [
  // Superseded when the script dock's default flipped from bottom to right.
  "mpp:advchart:pine-dock:v1",
  // Superseded when the script dock's open state became per Advanced Chart
  // instead of one flag for the whole app. Retired on the server as well as
  // locally — it was a synced key, so a local-only purge resurrects on hydrate.
  "mpp:advchart:pine-open:v1",
] as const;

export function isLegacyKey(key: string): boolean {
  return (
    LEGACY_PREFIXES.some((p) => key.startsWith(p)) ||
    (LEGACY_KEYS as readonly string[]).includes(key) ||
    isStrayAccountModeSlot(key)
  );
}

/**
 * A key kept on THIS device only: never mirrored to the server, never written
 * from it on hydrate, and never given a Display Profile slot.
 *
 * Not the same thing as a per-machine LAYOUT key (below). A layout key is
 * namespaced per Display Profile (lib/display/layoutNamespace.ts), so each
 * screen keeps its own arrangement. The Real / Paper choice is about the
 * device, not the screen: a demonstration must stay in Paper whichever
 * profile is loaded, and must leave Paper when told to.
 *
 * On 2026-10-03 the choice was listed as a layout key by exact match. The
 * namespace then stored it in the profile's slot (`<key>\0@dp:<id>`), the
 * exact match did not recognise that slot as per-machine, so the slot was
 * SYNCED: pressing Real wrote the slot and reloaded before the push, hydrate
 * wrote the server's "paper" back over it, and the app could not leave Paper.
 */
export function isDeviceOnlyKey(key: string): boolean {
  return key === ACCOUNT_MODE_KEY;
}

/** A Display Profile slot of the Real / Paper choice, left by that build. */
function isStrayAccountModeSlot(key: string): boolean {
  return key.startsWith(`${ACCOUNT_MODE_KEY}\u0000@dp:`);
}

/** Returns the keys removed, so the caller can also retire them server-side. */
export function purgeLegacyKeys(): string[] {
  if (typeof window === "undefined") return [];
  const doomed: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && isLegacyKey(k)) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {
    /* storage unavailable — nothing to purge */
  }
  if (doomed.length > 0) {
    console.info(`[prefs] purged ${doomed.length} legacy keys`);
  }
  return doomed;
}

function isDisposable(key: string): boolean {
  return DISPOSABLE_PREFIXES.some((p) => key.startsWith(p));
}

/**
 * Layout state kept PER-MACHINE — never mirrored to the server, and never
 * overwritten by another machine's copy on hydrate. Different screens (laptop
 * vs external monitor) need different pane sizes and arrangements, so syncing
 * these made two machines clobber each other. Display Profiles (which DO sync,
 * as definitions) are the deliberate way to copy an arrangement to another
 * machine. Covers: split-pane divider sizes (react-resizable-panels), which
 * library layout each pane shows (mpp:nested-ws:*), react-grid-layout page
 * arrangements + lock state (mpp:*:pw:v*), and the two legacy brief grids.
 */
export function isPerMachineLayoutKey(key: string): boolean {
  if (key.startsWith("react-resizable-panels:")) return true;
  if (key.startsWith("mpp:nested-ws:")) return true;
  // A Display Profile's own version of ONE workspace's pane tree. The library
  // (`mpp:workspaces:v1`) stays shared and synced — this is just how a 49"
  // ultrawide can show more panes of the SAME workspace than a laptop does,
  // without duplicating the workspace once per screen.
  if (key.startsWith("mpp:ws-tree:")) return true;
  if (key.includes(":pw:v")) return true;
  if (key.startsWith("mpp:daily-brief:v")) return true;
  if (key.startsWith("mpp:timing-brief:v")) return true;
  // Chart pane heights are screen-geometry — another machine's stretch must
  // not clobber this laptop on hydrate (same rationale as divider sizes).
  if (key.startsWith("mpp:advchart:pane-stretch:")) return true;
  if (key.startsWith("mpp:chart:pane-stretch:")) return true;
  // Advanced Chart multi-grid id + active cell — per desk instance, screen-local
  // like other arrangement keys (lab vs Development Testing must not fight).
  if (key.startsWith("mpp:advchart:desk-layout:")) return true;
  if (key.startsWith("mpp:advchart:desk-active-scope:")) return true;
  // Split-tree row heights are screen geometry, like a divider drag. Syncing
  // them let a reload pull the server's old band height back over a Save.
  if (key.startsWith("mpp:ws:rowh:")) return true;
  // Workspace / custom-page "maximize this pane" — arrangement, not content.
  if (key.startsWith("mpp:ws:maximized:")) return true;
  if (key.startsWith("mpp:ws:collapsed")) return true;
  // Metric-tile mouse sizes — screen geometry, like divider drags.
  if (key.startsWith("mpp:stat-tiles:")) return true;
  if (key.startsWith("mpp:options:trade-stats-tiles:")) return true;
  // The dragged height of a scroll box (components/ui/ResizableBox.tsx).
  if (key.startsWith("mpp:box-h:")) return true;
  // The chart quick look's dragged size (lib/chart/quickLookSize.ts).
  if (key.startsWith("mpp:chart-quick-look:size:")) return true;
  if (key.startsWith("mpp:pane-nav:maximized:")) return true;
  // Eye-menu Hide indicators / drawings — session chrome on this screen. Must
  // NOT ride prefs sync: a stale server "0" was overwriting a fresh local hide
  // on the next hydrate (leave Development Testing → About → return looked
  // like persistence never worked).
  if (key.startsWith("mpp:advchart:indicators-hidden:")) return true;
  if (key.startsWith("mpp:advchart:drawings-hidden:")) return true;
  if (key.startsWith("mpp:advchart:indicators-vis-snap:")) return true;
  // Script dock open/closed, per chart instance — the same class of per-screen
  // chrome as the two above, and one row per Advanced Chart is not something to
  // mirror to the server. The dock's POSITION (`pine-dock:v2`) still syncs.
  if (key.startsWith("mpp:advchart:pine-open:v2:")) return true;
  // Hide chrome (top / left / right / bottom) — one row per Advanced Chart.
  // A global or per-page-family blob leaked Hide onto every other chart.
  if (key.startsWith("mpp:advchart:chrome-edges:v2:")) return true;
  // Data Window open / cursor lock — one row per Advanced Chart. A global
  // chrome:v1 boolean opened the window on every other desk (Dashboard →
  // Timing Brief).
  if (key.startsWith("mpp:advchart:chrome-local:v1:")) return true;
  if (key === "mpp:advchart:chrome-view-seed:v1") return true;
  if (key.startsWith("mpp:fastchart:drawings-hidden:")) return true;
  // IBKR ticket habits + anything else under this prefix. Selected account
  // is a server row; a confirm-before-send toggle must not hydrate live
  // onto another machine.
  if (key.startsWith("mpp:ibkr:")) return true;
  return false;
}

/** A "synced" key is UI preference state we mirror to the server so it follows
 *  the user across browsers — everything under `mpp:` EXCEPT the disposable bar
 *  cache and the per-machine layout state (which each screen owns locally). */
export function isSyncedKey(key: string): boolean {
  if (isDisposable(key)) return false;
  if (isDeviceOnlyKey(key)) return false;
  if (isPerMachineLayoutKey(key)) return false;
  return key.startsWith("mpp:");
}

// Optional hook — set by lib/prefsSync.ts. Called after every
// successful write of a synced key so the change can be debounce-
// pushed to the server; `null` means the key was DELETED and the
// server row must go too. Kept as a single nullable callback (not an
// event bus) since there's exactly one consumer.
type SyncedWriteHook = (key: string, value: string | null) => void;
let syncedWriteHook: SyncedWriteHook | null = null;

/** Register (or clear, with null) the synced-write hook. */
export function setSyncedWriteHook(hook: SyncedWriteHook | null): void {
  syncedWriteHook = hook;
}

/** Remove every disposable cache entry. Returns how many were freed. */
function evictDisposable(): number {
  let removed = 0;
  // Collect first — removing while iterating localStorage by index
  // shifts the indices.
  const doomed: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && isDisposable(k)) doomed.push(k);
  }
  for (const k of doomed) {
    try {
      localStorage.removeItem(k);
      removed++;
    } catch {
      /* ignore */
    }
  }
  return removed;
}

/**
 * localStorage.setItem with quota recovery.
 *
 * Returns true if the value was stored, false if it ultimately
 * couldn't be (storage disabled, or even after eviction there's no
 * room — e.g. the value itself is larger than the quota).
 */
export function safeLocalStorageSet(rawKey: string, value: string): boolean {
  if (typeof window === "undefined") return false;
  const key = accountKey(rawKey);
  let stored = false;
  try {
    localStorage.setItem(key, value);
    stored = true;
  } catch {
    // Most likely QuotaExceededError. Free the disposable caches and
    // retry once. If THIS write is itself a disposable cache entry,
    // eviction may have removed siblings — that's fine, the retry
    // still has room.
    try {
      const freed = evictDisposable();
      if (freed > 0) {
        localStorage.setItem(key, value);
        stored = true;
      }
    } catch {
      // Still failing after eviction — give up gracefully. The
      // in-memory state is still correct for this session; only
      // cross-reload persistence is lost.
      stored = false;
    }
  }
  // Mirror synced prefs to the server (cross-browser sync). Fire even
  // if the local write failed — the server copy is then the canonical
  // one and will hydrate this browser on next load. Wrapped so a
  // hook error can never break the local write path.
  if (syncedWriteHook && isSyncedKey(key)) {
    try {
      syncedWriteHook(key, value);
    } catch {
      /* ignore */
    }
  }
  return stored;
}

/** localStorage.getItem with SSR / disabled-storage guards. */
export function safeLocalStorageGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(accountKey(key));
  } catch {
    return null;
  }
}

/**
 * localStorage.removeItem with the same SSR / disabled-storage guards.
 *
 * Two callers, two semantics — pick with `opts.sync`:
 *   - default (false): local recovery. A stored value turned out unreadable
 *     and keeping it would fail identically on every load. NOT routed through
 *     the sync hook: a local blob we cannot parse is not evidence the
 *     server's copy is bad too.
 *   - `{ sync: true }`: a REAL deletion (user removed a layout/template).
 *     Must reach the server, because hydrate is server-canonical — a key
 *     deleted only locally comes straight back on the next load.
 */
export function safeLocalStorageRemove(rawKey: string, opts?: { sync?: boolean }): void {
  if (typeof window === "undefined") return;
  const key = accountKey(rawKey);
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage disabled — nothing to drop */
  }
  if (opts?.sync && syncedWriteHook && isSyncedKey(key)) {
    try {
      syncedWriteHook(key, null);
    } catch {
      /* ignore */
    }
  }
}

/** Manually drop all disposable cache entries — exposed so a "having
 *  trouble?" affordance or a dev tool can reclaim space on demand. */
export function clearDisposableCaches(): number {
  if (typeof window === "undefined") return 0;
  return evictDisposable();
}
